import {concat, isArray, union} from 'lodash';

import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {
  TeammateAccountStatusesEnum,
  TeammateModel,
} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {
  isTeammateNamespace,
  isTeamNamespace,
} from '../../../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {buildTeammateDisplayName} from '../../../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {
  getIsUserGlobalAdmin,
  getUserRoleByNamespace,
} from '../../../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {getTeammates} from '../../../../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {teammateCanAccessInbox} from '../../../../../helpers/modelHelpers';
import {isTeamAdmin} from '../../../../../helpers/models/accessHelpers';
import {LensModel} from '../../../../../models/api/lensModel';
import {getInboxesTable} from '../../../../../store/data/inboxes/inboxesSelectors';
import {getTagsTable} from '../../../../../store/data/tags/tagsSelectors';
import {getUser} from '../../../../../store/data/teammates/teammatesSelectors';

export enum LensIngredientsTypesEnum {
  TEAM_INBOX = 'team inbox',
  TEAM_TAG = 'team tag',
  ASSIGNEES = 'assignee',
}

export interface DisplayLensIngredient {
  id: number;
  name: string | null;
  type: LensIngredientsTypesEnum;
}

export interface LensValidation {
  isLensInvalid: boolean;
  inaccessibleInboxes: ReadonlyArray<DisplayLensIngredient>;
  deletedInboxes: ReadonlyArray<DisplayLensIngredient>;
  allInboxesDeleted: boolean;
  allInboxesInaccessible: boolean;
  deletedTags: ReadonlyArray<DisplayLensIngredient>;
  deletedAssignees: ReadonlyArray<DisplayLensIngredient>;
  /** Includes inboxes, tags, and assignees */
  deletedIngredients: ReadonlyArray<DisplayLensIngredient>; // LensIngredientRecord;
  deletedIngredientsTypeAndCount: Partial<Record<LensIngredientsTypesEnum, number>>;
}

function useLensPropsValidation() {
  const {
    byId: inboxes,
    deletedIds: deletedInboxIds,
    deletedById: deletedInboxesById,
  } = useAppSelector(getInboxesTable);
  const user = useAppSelector(getUser);
  const teammates = useAppSelector(getTeammates);
  const {deletedIds: deletedTagIds, deletedById: deletedTagsById} = useAppSelector(getTagsTable);
  const isUserGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const roleByNamespace = useAppSelector(getUserRoleByNamespace);

  const validateLensProps = (
    inboxIds: ReadonlyArray<number>,
    tagIds: ReadonlyArray<number>,
    notTagIds: ReadonlyArray<number>,
    assigneeIds: ReadonlyArray<number>,
    notAssigneeIds: ReadonlyArray<number>,
    namespace: string,
  ): LensValidation => {
    /** Inbox errors */
    const inaccessibleInboxIngredients: ReadonlyArray<DisplayLensIngredient> = inboxIds
      .map((inboxId): [InboxModel['id'], InboxModel | undefined] => [inboxId, inboxes[inboxId]])
      .filter(([_inboxId, inbox]) => inbox === undefined || !teammateCanAccessInbox(user, inbox))
      .map(([inboxId, inbox]) => ({
        id: inboxId,
        name: inbox?.name ?? null,
        type: LensIngredientsTypesEnum.TEAM_INBOX,
      }));

    const deletedInboxes = inboxIds
      .filter((inboxId) => deletedInboxIds.has(inboxId))
      .map((inboxId) => [inboxId, deletedInboxesById[inboxId]]);
    const deletedInboxIngredients: ReadonlyArray<DisplayLensIngredient> = deletedInboxes.map(
      ([inboxId, inbox]) => ({
        id: inboxId,
        name: inbox?.name ?? null,
        type: LensIngredientsTypesEnum.TEAM_INBOX,
      }),
    );

    const allInboxesDeleted = deletedInboxIngredients.length === inboxIds.length;
    const allInboxesInaccessible = inaccessibleInboxIngredients.length === inboxIds.length;

    /** Teammate/assignee errors */
    const deletedAssignees = union(assigneeIds, notAssigneeIds).map(
      (assigneeId): [TeammateModel['id'], TeammateModel | undefined] => [assigneeId, teammates[assigneeId]],
    );
    const deletedAssigneeIngredients: ReadonlyArray<DisplayLensIngredient> = deletedAssignees
      .filter(
        ([_assigneeId, assignee]) =>
          assignee === undefined ||
          assignee.accountStatus === TeammateAccountStatusesEnum.DELETED ||
          assignee.accountStatus === TeammateAccountStatusesEnum.BLOCKED,
      )
      .map(([assigneeId, assignee]) => ({
        id: assigneeId,
        name: assignee === undefined ? null : buildTeammateDisplayName(assignee),
        type: LensIngredientsTypesEnum.ASSIGNEES,
      }));

    /** Tags errors */
    const deletedTags = union(tagIds, notTagIds)
      .filter((tagId) => deletedTagIds.has(tagId))
      .map((tagId) => [tagId, deletedTagsById[tagId]]);
    const deletedTagIngredients: ReadonlyArray<DisplayLensIngredient> = deletedTags.map(([tagId, tag]) => ({
      id: tagId,
      name: tag?.name ?? null,
      type: LensIngredientsTypesEnum.TEAM_TAG,
    }));

    const deletedIngredients: ReadonlyArray<DisplayLensIngredient> = concat(
      deletedInboxIngredients,
      deletedAssigneeIngredients,
      deletedTagIngredients,
    );

    const deletedIngredientsTypeAndCount = deletedIngredients.reduce(
      (current: Partial<Record<LensIngredientsTypesEnum, number>>, ingredient) => {
        const {type} = ingredient;
        const currentIngredientArray = current[type];

        if (!currentIngredientArray) {
          return {...current, [type]: 1};
        }
        return {...current, [type]: currentIngredientArray + 1};
      },
      {},
    );

    const ingredientErrors = {
      inaccessibleInboxes: inaccessibleInboxIngredients,
      deletedInboxes: deletedInboxIngredients,
      allInboxesDeleted,
      allInboxesInaccessible,
      deletedTags: deletedTagIngredients,
      deletedAssignees: deletedAssigneeIngredients,
    };

    const isUserTeamAdmin =
      isTeamNamespace(namespace) && (isUserGlobalAdmin || isTeamAdmin(namespace, roleByNamespace));

    const isLensInvalid =
      (isTeammateNamespace(namespace) ||
        isUserTeamAdmin ||
        (isTeamNamespace(namespace) && ingredientErrors.inaccessibleInboxes.length > 0)) &&
      Object.values(ingredientErrors).some((ingredientError) => {
        if (isArray(ingredientError)) {
          return ingredientError.length > 0;
        }
        return Boolean(ingredientError);
      });

    return {...ingredientErrors, isLensInvalid, deletedIngredients, deletedIngredientsTypeAndCount};
  };

  return {validateLensProps};
}

export function useLensValidation() {
  const {validateLensProps} = useLensPropsValidation();

  const validateLens = (lens: LensModel) => {
    const {inboxIds, tagIds, notTagIds, assigneeIds, notAssigneeIds, namespace} = lens;
    return validateLensProps(inboxIds, tagIds, notTagIds, assigneeIds, notAssigneeIds, namespace);
  };

  return {validateLens};
}
